import { supabase } from '@/integrations/supabase/client';
import { useDebounce } from '@/hooks/useDebounce';
import OpenAI from 'openai';

const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true
});

export type AssessmentEvaluation = {
  overallRating: number;
  skillRatings: {
    skill: string;
    rating: number;
  }[];
  summary: string;
};

export async function evaluateAssessment(
  transcript: { source: string; message: string; timestamp: string }[],
  skills: string[]
): Promise<AssessmentEvaluation> {
  try {
    const messages = transcript.map(entry => `${entry.source}: ${entry.message}`).join('\n');
    
    const prompt = `
      Please evaluate this candidate's assessment based on the following interview transcript:
      
      ${messages}
      
      Please evaluate the candidate's performance for these specific skills: ${skills.join(', ')}
      
      Provide your evaluation in the following JSON format:
      {
        "overallRating": (a number between 1-5),
        "skillRatings": [
          {
            "skill": "skill name",
            "rating": (a number between 1-5)
          }
          // repeat for each skill
        ],
        "summary": "2-3 lines summarizing the candidate's overall performance"
      }
      
      IMPORTANT: All ratings MUST be numeric values between 1-5. Do not use strings like "N/A" or any non-numeric values for ratings.
    `;

    const completion = await openai.chat.completions.create({
      messages: [{ role: 'user', content: prompt }],
      model: 'gpt-4-turbo-preview',
      response_format: { type: 'json_object' }
    });

    const responseText = completion.choices[0].message.content || '{}';
    // console.log('[Debug] Raw evaluation response:', responseText);
    let response: any;
    
    try {
      response = JSON.parse(responseText);
    } catch (e) {
      console.error('Error parsing evaluation response:', e);
      response = {};
    }

    // Ensure overallRating is numeric
    let overallRating = parseFloat(response.overallRating);
    if (isNaN(overallRating) || overallRating < 1 || overallRating > 5) {
      overallRating = 3; // Default to middle value if invalid
    }

    // Ensure all skill ratings are numeric
    const skillRatings = (response.skillRatings || []).map((skillRating: any) => {
      const rating = parseFloat(skillRating.rating);
      return {
        skill: skillRating.skill || 'Unknown Skill',
        rating: isNaN(rating) || rating < 1 || rating > 5 ? 3 : rating
      };
    });

    // Add missing skills with default ratings
    const existingSkills = skillRatings.map((sr: any) => sr.skill.toLowerCase());
    skills.forEach(skill => {
      if (!existingSkills.includes(skill.toLowerCase())) {
        skillRatings.push({
          skill: skill,
          rating: 3 // Default to middle value
        });
      }
    });

    return {
      overallRating: parseFloat(overallRating.toFixed(1)),
      skillRatings: skillRatings,
      summary: response.summary || 'No evaluation available.'
    };
  } catch (error) {
    console.error('Error evaluating assessment:', error);
    
    // Return a fallback evaluation
    return {
      overallRating: 3.0,
      skillRatings: skills.map(skill => ({
        skill,
        rating: 3.0
      })),
      summary: 'Unable to evaluate assessment due to an error.'
    };
  }
}

export const validateJobRole = async (role: string): Promise<boolean> => {
  if (!role.trim()) return false;
  
  try {
    const { data, error } = await supabase.functions.invoke('openai-assessment-helper', {
      body: { action: 'validateRole', data: { role } },
    });

    if (error) throw error;
    return data?.isValid || false;
  } catch (error) {
    console.error('Error validating job role:', error);
    return true; // Fail open to prevent blocking users
  }
};

export const suggestJobRoles = async (partial: string): Promise<string[]> => {
  if (!partial || partial.length < 2) return [];
  
  try {
    const { data, error } = await supabase.functions.invoke('openai-assessment-helper', {
      body: { action: 'suggestRoles', data: { partial } },
    });

    if (error) throw error;
    return data?.suggestions || [];
  } catch (error) {
    console.error('Error suggesting job roles:', error);
    return [];
  }
};

export const suggestSkillsForRole = async (role: string, level: 'entry' | 'mid' | 'senior'): Promise<string[]> => {
  try {
    const { data, error } = await supabase.functions.invoke('openai-assessment-helper', {
      body: { action: 'suggestSkills', data: { role, level } },
    });

    if (error) throw error;
    return data?.skills || [];
  } catch (error) {
    console.error('Error suggesting skills for role:', error);
    return [];
  }
};
